import { useState, useEffect, useRef, useCallback } from "react";
import apiClient from "../../shared/API/apiClient";
import { useReportingYear } from "../../context/ReportingYearContext";

const extractYearValue = (yearLabel) => {
  if (!yearLabel) return null;
  const match = yearLabel.match(/(\d{4}-\d{2,4})/);
  return match ? match[1] : yearLabel;
};

const TopBar = ({ user, onLogout }) => {
  const { initYears, changeYear, latestYear } = useReportingYear();
  const [years,   setYears]   = useState([]);
  const [year,    setYear]    = useState(null);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await apiClient.post("/api/reportingyear");
        const data = response.data;
        if (data.code === 200 && data.data) {
          setYears(data.data);

          const sorted   = [...data.data].sort((a, b) => b.id - a.id);
          const latest   = sorted[0];
          const latestVal = extractYearValue(latest?.year_label);

          const selected    = data.data.find((y) => y.is_selected === 1) ?? latest;
          const selectedVal = extractYearValue(selected?.year_label);

          setYear(selected);
          initYears(selectedVal, latestVal); // ← atomic: sets both + isCurrentYear
        }
      } catch (err) {
        console.error("Failed to fetch fiscal years:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchYears();
  }, [initYears]); // ← satisfies exhaustive-deps, initYears is stable (useCallback)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleYearSelect = useCallback((y) => {
    const selectedVal = extractYearValue(y.year_label);
    setYear(y);
    setOpen(false);
    changeYear(selectedVal, latestYear); // ← updates year + isCurrentYear atomically
  }, [changeYear, latestYear]);

  const displayName = user?.name || user?.email || "Compliance Officer";
  const initials = displayName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="topbar-global">
      <div className="topbar-left">
        <span className="topbar-label">Reporting Year:</span>
        <div className="year-dropdown-wrap" ref={dropdownRef}>
          <button
            className="year-dropdown-btn"
            onClick={() => setOpen((o) => !o)}
            disabled={loading}
          >
            {loading ? "Loading..." : year?.year_label ?? "Select Year"}
            <span className="dropdown-arrow">▾</span>
          </button>

          {open && (
            <div className="year-dropdown-menu">
              {years.map((y) => (
                <div
                  key={y.id}
                  className={`year-option ${y.id === year?.id ? "selected" : ""}`}
                  onClick={() => handleYearSelect(y)}
                >
                  <span className="year-option-check">
                    {y.id === year?.id ? "✓" : ""}
                  </span>
                  <span style={{ flex: 1 }}>{y.year_label}</span>
                  {y.is_locked === 1 && <span className="lock-icon">🔒</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="topbar-right">
        <span className="topbar-username">{displayName}</span>
        <div className="topbar-avatar">{initials}</div>
        <button className="topbar-logout-icon" title="Sign out" onClick={onLogout}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default TopBar;